import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { z } from 'zod/v4';
import { type ApiError, type ApiResponse, useLegacyAxiosInstance } from './utils';
import { addActionNameToMutationResult } from './utils/add-action-name-to-mutation-result';

const deleteContactPersonVariablesSchema = z.object({
  debtorId: z.number().or(z.string().transform(Number)),
  contactPersonId: z.string(),
});

export type DeleteContactPersonVariables = z.infer<typeof deleteContactPersonVariablesSchema>;

export async function deleteContactPersonFn(
  axiosInstance: Promise<AxiosInstance>,
  data: DeleteContactPersonVariables,
) {
  const instance = await axiosInstance;
  return instance.delete(`debtors/${data.debtorId}/contact_persons/${data.contactPersonId}`);
}

export function useDeleteContactPerson() {
  const client = useQueryClient();
  const axiosInstance = useLegacyAxiosInstance();

  const mutation = useMutation<ApiResponse, ApiError, DeleteContactPersonVariables>({
    mutationFn: variables => deleteContactPersonFn(axiosInstance, variables),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['contact-persons'] });
    },
  });

  return addActionNameToMutationResult<
    ApiResponse,
    ApiError,
    DeleteContactPersonVariables,
    'deleteContactPerson'
  >('deleteContactPerson', mutation);
}
